import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { resolveExportNotificationTarget } from "@/utils/exportNotification";

/**
 * Opens the destination of an `EXPORT_FINISHED` notification, switching the
 * session to the export's account first when a superadmin is elsewhere (same
 * order as useOpenAccount: switch, then navigate). A failed switch still
 * navigates: InternalLayout's URL-driven auto-switch reconciles it.
 *
 * @returns {(notification) => boolean} true when the notification was handled.
 */
export function useOpenExportNotification() {
  const navigate = useNavigate();
  const { user, isSuperAdmin, switchAccount } = useAuth();

  return useCallback(
    (notification) => {
      const target = resolveExportNotificationTarget(notification, {
        currentAccountId: user?.accountId,
        isSuperAdmin: isSuperAdmin?.() === true,
      });
      if (!target) return false;

      if (!target.switchToAccountId) {
        navigate(target.path);
        return true;
      }
      switchAccount(target.switchToAccountId)
        .catch((error) => console.error("[useOpenExportNotification] switch failed:", error))
        .finally(() => navigate(target.path));
      return true;
    },
    [navigate, user?.accountId, isSuperAdmin, switchAccount],
  );
}

export default useOpenExportNotification;
